/**
 * Agent hand-offs (spec #46 §2, exposed by #44's accepted delta 4).
 *
 * The workspace reads the two human-consumed types only. `resolve` is
 * restricted server-side to those same two: a cycle-consumed hand-off is
 * closed by the cycle that consumed it, never by a person clicking here.
 */

import { createHeaders } from './apiClient';
import { handleResponse } from './httpResponse';
import type {
  HandoffListQuery,
  HandoffListResponse,
  HandoffOutcome,
  HandoffResolveResponse,
} from '../types/handoffs';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export async function listHandoffs(
  query: HandoffListQuery = {}
): Promise<HandoffListResponse> {
  const params = new URLSearchParams();
  if (query.to_domain) params.set('to_domain', query.to_domain);
  if (query.status) params.set('status', query.status);
  if (query.types?.length) params.set('types', query.types.join(','));
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.offset !== undefined) params.set('offset', String(query.offset));

  const qs = params.toString();
  const response = await fetch(
    `${API_BASE_URL}/api/agents/handoffs${qs ? `?${qs}` : ''}`,
    { method: 'GET', headers: createHeaders() }
  );
  return handleResponse<HandoffListResponse>(response);
}

export async function resolveHandoff(
  id: string,
  outcome: HandoffOutcome,
  reason?: string
): Promise<HandoffResolveResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/agents/handoffs/${encodeURIComponent(id)}/resolve`,
    {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify({ outcome, reason }),
    }
  );
  return handleResponse<HandoffResolveResponse>(response);
}
